import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import type { UploadFrameRequest } from '@photobooth/types';

interface BatchFrameUploadZoneProps {
  onUploadComplete: () => void;
  frameCategories: Array<{ frameCategoryId: string; name: string }>;
  uploadFile: (file: File, metadata: UploadFrameRequest) => Promise<{ success: boolean; error?: string }>;
}

export function BatchFrameUploadZone({
  onUploadComplete,
  frameCategories,
  uploadFile,
}: BatchFrameUploadZoneProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [paperType, setPaperType] = useState<'collage_4x6' | 'strip'>('collage_4x6');
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [results, setResults] = useState<{ success: number; failed: string[] } | null>(null);
  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFiles(acceptedFiles);
    setResults(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxFiles: 50,
    disabled: isUploading,
  });

  const getNameFromFile = (file: File): string => {
    return file.name.replace(/\.[^/.]+$/, '');
  };

  const handleBatchUpload = async () => {
    if (!categoryId) {
      setError('Please select a category');
      return;
    }
    if (selectedFiles.length === 0) {
      setError('Please select at least one image');
      return;
    }

    setError('');
    setIsUploading(true);
    setProgress({ current: 0, total: selectedFiles.length });

    const failed: string[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      if (!file) {
        // This should not happen because we are iterating over selectedFiles length,
        // but this guard keeps TypeScript and runtime safe.
        continue;
      }
      setProgress({ current: i + 1, total: selectedFiles.length });

      const metadata: UploadFrameRequest = {
        name: getNameFromFile(file),
        frameCategoryId: categoryId,
        paperType,
      };

      const result = await uploadFile(file, metadata);
      if (!result.success) {
        failed.push(`${file.name}: ${result.error ?? 'Upload failed'}`);
      }
    }

    setProgress(null);
    setIsUploading(false);
    setResults({
      success: selectedFiles.length - failed.length,
      failed,
    });
    setSelectedFiles([]);

    if (failed.length === 0) {
      onUploadComplete();
    }
  };

  const clearResults = () => {
    setResults(null);
    if (selectedFiles.length === 0) {
      onUploadComplete();
    }
  };

  return (
    <div className="bg-surface rounded-lg shadow p-6">
      <h2 className="text-xl font-bold text-text mb-4">Batch Upload Frames</h2>
      <p className="text-sm text-text-muted mb-4">
        Upload multiple frames at once. All files will use the same category and paper type.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-text mb-1">Category *</label>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            disabled={isUploading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select a category</option>
            {frameCategories.map((cat) => (
              <option key={cat.frameCategoryId} value={cat.frameCategoryId}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-text mb-1">Paper Type *</label>
          <select
            value={paperType}
            onChange={(e) => setPaperType(e.target.value as 'collage_4x6' | 'strip')}
            disabled={isUploading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="collage_4x6">4x6 Collage</option>
            <option value="strip">Strip</option>
          </select>
        </div>
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-primary bg-blue-50' : 'border-gray-300 hover:border-primary'
        } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="text-4xl mb-2">📁</div>
        <p className="text-text mb-1">
          {isDragActive ? 'Drop images here' : 'Drag & drop multiple images here'}
        </p>
        <p className="text-sm text-text-muted">or click to select (PNG or JPEG, max 50 files)</p>
        {selectedFiles.length > 0 && (
          <p className="text-sm font-medium text-primary mt-2">{selectedFiles.length} file(s) selected</p>
        )}
      </div>

      {selectedFiles.length > 0 && !isUploading && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-muted">
            {selectedFiles.length} frame(s) ready. Names will be derived from filenames.
          </p>
          <button
            onClick={handleBatchUpload}
            className="bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700"
          >
            Upload All
          </button>
        </div>
      )}

      {isUploading && progress && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-text">
            Uploading {progress.current} of {progress.total}...
          </p>
          <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {results && (
        <div className="mt-4 p-4 rounded-lg bg-gray-50">
          <p className="text-sm font-medium text-text">
            Complete: {results.success} uploaded successfully
            {results.failed.length > 0 && `, ${results.failed.length} failed`}
          </p>
          {results.failed.length > 0 && (
            <ul className="mt-2 text-sm text-red-600 list-disc list-inside">
              {results.failed.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          )}
          <button
            onClick={clearResults}
            className="mt-3 text-sm text-primary hover:underline"
          >
            {results.success > 0 ? 'Done' : 'Try again'}
          </button>
        </div>
      )}
    </div>
  );
}
